import { Product } from "@CommonApi/inputs/products/all";
import { StoreProduct } from "@CommonApi/inputs/store-products";
import { useRef, useState } from "react";
import { useApi } from "src/api";
import { Checkbox, Color, Flex, Icon } from "src/elements";
import { textVariants } from "src/elements/Text/store/variants";
import { useDebounceEffect } from "src/hooks/useDebounceEffect";
import { useHoverClickable } from "src/utils/hoverClickable";
import styled from "styled-components";

const EditableInput = styled("input")`
  margin: 0;
  padding: 0;
  border: 0;
  height: min-content;
  min-height: 20px;
  font-size: ${textVariants.small2.fontSize};
  border-radius: 3px;
  outline: none;
  background: none;
`;

interface Props {
  id: string;
  product: StoreProduct & Pick<Product, "product_code" | "upc" | "name">;
  isNumber?: boolean;
  isNullable?: boolean;
  isBoolean?: boolean;
  updateProduct: (
    updatedProduct: StoreProduct & Pick<Product, "product_code" | "upc" | "name">,
  ) => void;
}

export const EditableField = ({
  id,
  product,
  isNumber,
  isNullable,
  isBoolean,
  updateProduct,
}: Props) => {
  const { putStoreProductsApi } = useApi();

  const initialProductValue = product[id as keyof typeof product];
  const [value, _setValue] = useState<string>(
    initialProductValue === null ? "NULL" : String(initialProductValue),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const refInput = useRef(null);
  const refFirstRun = useRef(true);
  const { isFocused } = useHoverClickable(refInput);

  useDebounceEffect(
    () => {
      saveProduct(value);
    },
    [value],
    600,
  );

  useDebounceEffect(
    () => {
      if (isSaved) {
        setIsSaved(false);
      }
    },
    [isSaved],
    1000,
  );

  const saveProduct = async (value: string) => {
    if (refFirstRun.current === true) return;

    setIsSaving(true);
    const updatedValue =
      isNullable && value === "NULL"
        ? null
        : isNumber
          ? Number(value)
          : isBoolean
            ? value === "true"
            : value;

    const updatedProduct = {
      ...product,
      [id]: updatedValue,
    };

    const response = await putStoreProductsApi({
      store_products: [updatedProduct],
    });
    setIsSaving(false);
    refFirstRun.current = true;

    if (response) {
      setIsSaved(true);
      updateProduct(updatedProduct);
    }
  };

  const setValue = (value: string) => {
    refFirstRun.current = false;
    _setValue(value);
  };

  return (
    <Flex
      align="center"
      gap="5px"
      padding="0 2px"
      background={isFocused && Color.greenSmoke}
      borderRadius="3px"
      maxHeight="min-content"
    >
      {isBoolean ? (
        <Checkbox
          isChecked={value === "true"}
          setIsChecked={(checked: boolean) => setValue(String(checked))}
        />
      ) : (
        <EditableInput
          ref={refInput}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          autoCorrect="off"
          autoComplete="off"
        />
      )}

      <Flex minHeight="14px" minWidth="14px">
        {!isSaving && isSaved && <Icon name="checkmarkCircle" size={14} />}
        {isSaving && <Icon name="spinner" size={14} />}
      </Flex>
    </Flex>
  );
};
